import {SC_Originator} from "../enums/SC_Originator";
import {SC_Department} from "../enums/SC_Department";
import EditableCardEntryInput from "../../../components/editable_entries/EditableCardEntryInput";
import SimpleCardBox from "../../../components/cards/SimpleCardBox";
import {formatNoteDate} from "../../DateUtils";
import {SC_User} from "../SC_User";
import UserPreviewModal from "../../../components/modals/previews/UserPreviewModal";
import {ApiContext} from "../../Requests";
import {useContext} from "@wordpress/element";
import FontAwesomeButton from "../../../components/cards/FontAwesomeButton";
import myCircleInfo from "../../../customicons/myCircleInfo";

export interface SC_BasicNoteConstructorProps {
    id?: number,
    number?: number,
    payload?: string,
    internalAuthorID?: number,
    noteCreatedTime?: Date,
    originator: SC_Originator,
    followUpTarget?: number,
    followUpDepartment?: number,
    resolutionNote?: number,
    isPrivileged?: boolean,
    followUpUsers?: number[],
    followUpDepartments?: SC_Department[],
}

export class SC_BasicNote {
    static inputKeys = {
        id: 'id',
        number: 'number',
        payload: 'noteData',
        internalAuthorID: 'internalAuthorID',
        noteCreatedTime: 'createDate',
        originator: 'originator',
        followUpTarget: 'followUpTarget',
        followUpDepartment: 'followUpDepartment',
        resolutionNote: 'resolutionNote',
        isPrivileged: 'isPrivileged',
    } as const;

    public id: number | null;
    public number: number | null;
    public payload: string | null;
    public internalAuthorID: number | null;
    public internalAuthor: SC_User | null;
    public noteCreatedTime: Date | null;
    public originator: SC_Originator;
    public followUpTarget: number | null;
    public followUpTargetUser: SC_User | null;
    public followUpDepartment: SC_Department | null;
    public resolutionNote: number | null;
    public resolutionNoteActual: SC_BasicNote | null;
    public isPrivileged: boolean | null;

    constructor({
                    id = null,
                    number = null,
                    payload = null,
                    internalAuthorID = null,
                    noteCreatedTime = null,
                    originator = new SC_Originator(),
                    followUpTarget = null,
                    followUpDepartment = null,
                    resolutionNote = null,
                    isPrivileged = null,
                }: SC_BasicNoteConstructorProps) {
        this.id = id;
        this.number = number;
        this.payload = payload;
        this.internalAuthorID = internalAuthorID;
        this.internalAuthor = null;
        this.noteCreatedTime = noteCreatedTime;
        this.originator = originator;
        this.followUpTarget = followUpTarget;
        this.followUpTargetUser = null;
        this.followUpDepartment = followUpDepartment ? SC_Department.fromAPI(followUpDepartment) : null;
        this.resolutionNote = resolutionNote;
        this.isPrivileged = isPrivileged;
    }

    rehydrateNotes(notes: SC_BasicNote[]): void {
        if (this.resolutionNote && notes?.length)
            this.resolutionNoteActual = notes.find(note => note.id === this.resolutionNote) ?? null;
    }

    static basicAPIStuff(jsonin: Record<string, any>): SC_BasicNoteConstructorProps {
        return {
            id: jsonin[SC_BasicNote.inputKeys.id] ?? null,
            number: jsonin[SC_BasicNote.inputKeys.number] ?? null,
            internalAuthorID: jsonin[SC_BasicNote.inputKeys.internalAuthorID] ?? null,
            payload: jsonin[SC_BasicNote.inputKeys.payload] ?? null,
            noteCreatedTime: jsonin[SC_BasicNote.inputKeys.noteCreatedTime] ? new Date(jsonin[SC_BasicNote.inputKeys.noteCreatedTime]) : null,
            originator: SC_Originator.fromAPI(jsonin[SC_BasicNote.inputKeys.originator]),
            followUpTarget: jsonin[SC_BasicNote.inputKeys.followUpTarget] ?? null,
            followUpDepartment: jsonin[SC_BasicNote.inputKeys.followUpDepartment] ?? null,
            resolutionNote: jsonin[SC_BasicNote.inputKeys.resolutionNote] ?? null,
            isPrivileged: jsonin[SC_BasicNote.inputKeys.isPrivileged] ?? null,
        };
    }

    scavengeUsers(users: SC_User[]): void {
        if (this.internalAuthorID && users) {
            let internalAuthor = users.find(user => user.insiteID === this.internalAuthorID);
            if (internalAuthor)
                this.internalAuthor = internalAuthor;
        }
        if (this.followUpTarget && users) {
            let followUpTargetUser = users.find(user => user.insiteID === this.followUpTarget);
            if (followUpTargetUser)
                this.followUpTargetUser = followUpTargetUser;
        }
    }

    noteItem(someFup: boolean, requestResolution: (note: SC_BasicNote) => void) {
        return <NoteItem note={this} showFollowUp={someFup} requestResolution={requestResolution}/>;
    }

    get displayText(): string | null {
        return this.payload;
    }

    get displayHTML(): string | null {
        return this.payload;
    }

    get outstandingFollowUp(): boolean {
        return (this.followUpDepartment || this.followUpTarget) && !this.resolutionNote;
    }

    followUpDisplay(requestResolution: (note: SC_BasicNote) => void): Element | null {
        const { caches } = useContext(ApiContext);

        return <>
            {this.followUpTargetUser && <><UserPreviewModal user={this.followUpTargetUser} /><br/></>}
            {this.followUpDepartment && <><i>Someone in {this.followUpDepartment.shortName}</i><br/></>}
            {this.followUpTarget === myid || caches.permissions.ref.current.departments.some(it => this.followUpDepartment.is(it.toAPI())) ? <>
                <button onClick={() => requestResolution(this)}>Resolve</button>
            </> : null}
        </>
    }
}

const NoteItem = ({note, showFollowUp, requestResolution}: {note: SC_BasicNote, showFollowUp: boolean, requestResolution: (note: SC_BasicNote) => void}) => {
    if (!note.payload)
        return null;

    let user: string;

    if (note.internalAuthor?.name)
        user = note.internalAuthor.name;
    else if (note.internalAuthor)
        user = "Someone at SiteCrew";
    else
        user = "Unknown";

    return <tr>
        <td style={{textAlign: "center"}}><a style={{display: "contents", cursor: "help"}} title={"Insite Note"}><img style={{height: "1rem"}} src={"/wp-content/themes/developmenttheme/assets/images/sitecrew_swoosh.svg"} alt={"SiteCrew Logo"}/></a></td>
        <td style={{textWrap: "nowrap"}}>{formatNoteDate(note.noteCreatedTime)}</td>
        <td>{user}</td>
        {showFollowUp ?
            <td>
                {note.outstandingFollowUp ? note.followUpDisplay(requestResolution) : note.resolutionNote ? note.resolutionNoteActual ? <><span>Resolved by {note.resolutionNoteActual.internalAuthor.displayName}</span><br/><span>On {formatNoteDate(note.resolutionNoteActual.noteCreatedTime)}</span><FontAwesomeButton icon={myCircleInfo} title={note.resolutionNoteActual.displayText}/></> : <i>Resolved...</i> : null}
            </td> : null}
        <td>{note.payload}</td>
    </tr>;
}
